import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NewInvoiceModalModern from "./NewInvoiceModalModern";
import NewPaymentModalModern from "./NewPaymentModalModern";
import generateClientStatement from "./utils/generateClientStatement";
import StatementExportModal from "./StatementExportModal";

import {
  ArrowLeft,
  Wallet,
  CreditCard,
  Receipt,
  BadgeCheck,
  Smartphone,
  Bell,
  TrendingUp,
  FileText,
  Download,
  ChevronDown
} from "lucide-react";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  Tooltip,
} from "recharts";

import "./styles/ClientAccount.css";
import { toast } from "react-toastify";



const BASE_URL = "https://receivablesbackend.onrender.com/api";

const ClientAccount = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);

  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("overview");
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [
  showStatementModal,
  setShowStatementModal,
] = useState(false);


const [invoiceFilter, setInvoiceFilter] =
  useState("all");

const [paymentFilter, setPaymentFilter] =
  useState("all");

const [
  invoiceStartDate,
  setInvoiceStartDate,
] = useState("");

const [
  invoiceEndDate,
  setInvoiceEndDate,
] = useState("");

const [
  paymentStartDate,
  setPaymentStartDate,
] = useState("");

const [
  paymentEndDate,
  setPaymentEndDate,
] = useState("");

const [
  expandedInvoice,
  setExpandedInvoice
] = useState(null);


const [
  expandedPayment,
  setExpandedPayment
] = useState(null);


  // =====================================================
  // FETCH ALL DATA
  // =====================================================

  useEffect(() => {
    loadData();
  }, [clientId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // CUSTOMER
      const customerRes = await fetch(
        `${BASE_URL}/customers/${clientId}`
      );

      if (!customerRes.ok) {
        throw new Error("Failed to load customer");
      }

      const customer = await customerRes.json();

      // CREDIT
      const creditRes = await fetch(
        `${BASE_URL}/credit/${customer.external_id}`
      );

      const creditData = await creditRes.json();

      // INVOICES
      const invoicesRes = await fetch(`${BASE_URL}/invoices`);

      const allInvoices = await invoicesRes.json();

      // PAYMENTS
      const paymentsRes = await fetch(`${BASE_URL}/payments`);

      const allPayments = await paymentsRes.json();

      // FILTER
      const clientInvoices = allInvoices.filter(
        (inv) =>
          Number(inv.pos_customer_id) === Number(clientId)
      );

      const clientPayments = allPayments.filter(
        (p) =>
          Number(p.pos_customer_id) === Number(clientId)
      );

      setClient({
        ...customer,
        credit_balance: Number(
          creditData.credit_balance || 0
        ),
      });

      setInvoices(clientInvoices);
      setPayments(clientPayments);

    } catch (err) {
      console.error(err);
      toast.error("Failed to load account.");
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // CALCULATIONS
  // =====================================================

  const totalOutstanding = useMemo(() => {
    return invoices.reduce((sum, inv) => {

      const relatedPayments = payments.filter(
        (p) =>
          p.invoice_number === inv.invoice_number
      );

      const totalPaid = relatedPayments.reduce(
        (s, p) => s + Number(p.amount || 0),
        0
      );

      return (
        sum +
        (
          Number(inv.amount || 0) - totalPaid
        )
      );

    }, 0);
  }, [invoices, payments]);

  const totalPaid = useMemo(() => {
    return payments.reduce(
      (sum, p) =>
        sum + Number(p.amount || 0),
      0
    );
  }, [payments]);

  const assessment = useMemo(() => {

    if (totalOutstanding <= 0) {
      return "Perfect";
    }

    if (
      totalOutstanding > 0 &&
      totalOutstanding <= 50000
    ) {
      return "Good";
    }

    return "Bad";

  }, [totalOutstanding]);

  // =====================================================
  // CHART DATA
  // =====================================================

  const chartData = invoices.slice(0, 6).map((inv) => ({
    name: new Date(
      inv.date_created
    ).toLocaleDateString("en-US", {
      month: "short",
    }),

    amount: Number(inv.amount || 0),
  }));

const filterByDate = (
  items,
  filter,
  startDate,
  endDate,
  field
) => {

  const now = new Date();

  if (filter === "all") {
    return items;
  }

  // CURRENT MONTH

  if (filter === "current") {

    return items.filter((item) => {

      const d =
        new Date(item[field]);

      return (
        d.getMonth() ===
          now.getMonth() &&
        d.getFullYear() ===
          now.getFullYear()
      );
    });
  }

  // PREVIOUS MONTH

  if (filter === "previous") {

    const previousMonth =
      now.getMonth() - 1;

    return items.filter((item) => {

      const d =
        new Date(item[field]);

      return (
        d.getMonth() ===
          previousMonth &&
        d.getFullYear() ===
          now.getFullYear()
      );
    });
  }

  // CUSTOM RANGE

  if (
    filter === "custom" &&
    startDate &&
    endDate
  ) {

    const start =
      new Date(startDate);

    const end =
      new Date(endDate);

    return items.filter((item) => {

      const d =
        new Date(item[field]);

      return (
        d >= start &&
        d <= end
      );
    });
  }

  return items;
};





const filteredInvoices =
  filterByDate(
    invoices,
    invoiceFilter,
    invoiceStartDate,
    invoiceEndDate,
    "date_created"
  );

const filteredPayments =
  filterByDate(
    payments,
    paymentFilter,
    paymentStartDate,
    paymentEndDate,
    "payment_date"
  );



// =====================================
// FILTERED TOTALS
// =====================================

const filteredInvoiceTotal =
  filteredInvoices.reduce(
    (sum, inv) =>
      sum + Number(inv.amount || 0),
    0
  );

const filteredOutstanding =
  filteredInvoices.reduce(
    (sum, inv) => {

      const relatedPayments =
        payments.filter(
          (p) =>
            p.invoice_number ===
            inv.invoice_number
        );

      const totalPaid =
        relatedPayments.reduce(
          (s, p) =>
            s +
            Number(p.amount || 0),
          0
        );

      return (
        sum +
        (
          Number(inv.amount || 0) -
          totalPaid
        )
      );

    },
    0
  );

const filteredPaymentTotal =
  filteredPayments.reduce(
    (sum, p) =>
      sum + Number(p.amount || 0),
    0
  );

  // =====================================================
  // STATUS
  // =====================================================

  const getInvoiceStatus = (invoice) => {

    const relatedPayments = payments.filter(
      (p) =>
        p.invoice_number === invoice.invoice_number
    );

    const totalPaid = relatedPayments.reduce(
      (s, p) =>
        s + Number(p.amount || 0),
      0
    );

    const amount = Number(invoice.amount || 0);

    if (totalPaid >= amount) {
      return "Paid";
    }

    const dueDate = new Date(invoice.due_date);

    if (Date.now() > dueDate) {
      return "Overdue";
    }

    return "Outstanding";
  };

const toggleInvoice = (invoiceId) => {

  setExpandedInvoice(current =>
    current === invoiceId
      ? null
      : invoiceId
  );

};


const togglePayment = (paymentId) => {

  setExpandedPayment(current =>
    current === paymentId
      ? null
      : paymentId
  );

};



  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div className="account-loading-screen">

        <div className="account-loader"></div>

        <h2>Loading Client Workspace...</h2>

      </div>
    );
  }

  // =====================================================
  // NOT FOUND
  // =====================================================

  if (!client) {
    return (
      <div className="account-loading-screen">

        <h2>Client Not Found</h2>

      </div>
    );
  }

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="modern-account-page">

      <div className="account-container">

        {/* ===================================== */}
        {/* TOP BAR */}
        {/* ===================================== */}

        <div className="account-topbar">

          <button
            className="back-button"
            onClick={() =>
              navigate("/clients")
            }
          >
            <ArrowLeft size={18} />
            Back to Clients
          </button>

          <div className="top-actions">

            <button className="top-action-btn">
              <Bell size={16} />
              Reminder
            </button>

            <button className="top-action-btn primary-btn">
              <Smartphone size={16} />
              Send SMS
            </button>

          </div>
        </div>

        {/* ===================================== */}
        {/* HERO CARD */}
        {/* ===================================== */}

        <div className="client-hero-card">

          {/* LEFT */}

          <div className="hero-left">

            <div className="client-avatar-big">
              {client.name?.charAt(0)}
            </div>

            <div>

              <div className="hero-title-row">

                <h1>{client.name}</h1>

                <span
                  className={`hero-assessment ${assessment.toLowerCase()}`}
                >
                  <BadgeCheck size={14} />
                  {assessment}
                </span>

              </div>

              <p>
                {client.address || "No Address"}
              </p>

              <p>
                {client.phone || "No Contact"}
              </p>

            </div>

          </div>

          {/* RIGHT */}

          <div className="hero-stats">

            <div className="hero-stat-card">

              <Wallet size={20} />

              <span>Total Outstanding</span>

              <strong className="danger-text">
                $
                {totalOutstanding.toFixed(2)}
              </strong>

            </div>

            <div className="hero-stat-card">

              <CreditCard size={20} />

              <span>Credit Balance</span>

              <strong>
                $
                {Number(
                  client.credit_balance
                ).toFixed(2)}
              </strong>

            </div>

            <div className="hero-stat-card">

              <Receipt size={20} />

              <span>Total Invoices</span>

              <strong>
                {invoices.length}
              </strong>

            </div>

            <div className="hero-stat-card">

              <TrendingUp size={20} />

              <span>Total Paid</span>

              <strong className="success-text">
                $
                {totalPaid.toFixed(2)}
              </strong>

            </div>

          </div>

        </div>

        {/* ===================================== */}
        {/* TABS */}
        {/* ===================================== */}

        <div className="modern-tabs">

          <button
            className={
              selectedTab === "overview"
                ? "active-tab"
                : ""
            }
            onClick={() =>
              setSelectedTab("overview")
            }
          >
            Overview
          </button>

          <button
            className={
              selectedTab === "invoices"
                ? "active-tab"
                : ""
            }
            onClick={() =>
              setSelectedTab("invoices")
            }
          >
            Invoices
          </button>

          <button
            className={
              selectedTab === "payments"
                ? "active-tab"
                : ""
            }
            onClick={() =>
              setSelectedTab("payments")
            }
          >
            Payments
          </button>

        </div>

        {/* ===================================== */}
        {/* OVERVIEW */}
        {/* ===================================== */}

        {selectedTab === "overview" && (

          <>

            <div className="overview-grid">

              {/* CHART */}

              <div className="overview-card">

                <div className="card-header">
                  <h3>Outstanding Trend</h3>
                </div>

                <ResponsiveContainer
                  width="100%"
                  height={280}
                >

                  <AreaChart data={chartData}>

                    <XAxis dataKey="name" />

                    <Tooltip />

                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#009688"
                      fill="#009688"
                      fillOpacity={0.15}
                    />

                  </AreaChart>

                </ResponsiveContainer>

              </div>

              {/* QUICK ACTIONS */}

              <div className="overview-card">

                <div className="card-header">
                  <h3>Quick Actions</h3>
                </div>

                <div className="quick-actions-grid">

<button
  className="quick-action"
  onClick={() => setShowInvoiceModal(true)}
>
  <Receipt size={20} />
  Create Invoice
</button>

<button
  className="quick-action"
  onClick={() => setShowPaymentModal(true)}
>
  <Wallet size={20} />
  Record Payment
</button>

<button className="quick-action">
  <FileText size={20} />
  Send Statement
</button>

<button
  className="quick-action"
  onClick={() =>
    setShowStatementModal(true)
  }
>
  <Download size={20} />
  Export Report
</button>

                </div>

              </div>

            </div>

          </>

        )}

        {/* ===================================== */}
        {/* INVOICES */}
        {/* ===================================== */}

        {selectedTab === "invoices" && (

          <div className="modern-table-card">

            <div className="card-header">
              <h3>Invoices</h3>
            </div>

<div className="inline-filters">

  <button
    className={
      invoiceFilter === "all"
        ? "filter-active"
        : ""
    }
    onClick={() =>
      setInvoiceFilter("all")
    }
  >
    All Time
  </button>

  <button
    className={
      invoiceFilter === "current"
        ? "filter-active"
        : ""
    }
    onClick={() =>
      setInvoiceFilter("current")
    }
  >
    Current Month
  </button>

  <button
    className={
      invoiceFilter === "previous"
        ? "filter-active"
        : ""
    }
    onClick={() =>
      setInvoiceFilter("previous")
    }
  >
    Previous Month
  </button>

  <button
    className={
      invoiceFilter === "custom"
        ? "filter-active"
        : ""
    }
    onClick={() =>
      setInvoiceFilter("custom")
    }
  >
    Custom
  </button>

</div>

{invoiceFilter === "custom" && (

  <div className="date-range-inline">

    <input
      type="date"
      value={invoiceStartDate}
      onChange={(e) =>
        setInvoiceStartDate(
          e.target.value
        )
      }
    />

    <input
      type="date"
      value={invoiceEndDate}
      onChange={(e) =>
        setInvoiceEndDate(
          e.target.value
        )
      }
    />

  </div>

)}


            <table className="modern-account-table">

              <thead>

                <tr>
                  <th>Invoice</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Due Date</th>
                  <th>Status</th>
                </tr>

              </thead>

              <tbody>

                {filteredInvoices.length > 0 ? (

filteredInvoices.map((inv) => {

    const relatedPayments =
        payments.filter(
            p =>
                p.invoice_number ===
                inv.invoice_number
        );

    const paid =
        relatedPayments.reduce(
            (sum, p) =>
                sum +
                Number(p.amount || 0),
            0
        );

    const outstanding =
        Number(inv.amount || 0) - paid;

    return (

        <React.Fragment key={inv.id}>

            {/* MAIN ROW */}

            <tr
                className="invoice-row"
                onClick={() =>
                    toggleInvoice(inv.id)
                }
            >

                <td>

                    <div
                        className="invoice-expand"
                    >

                        <ChevronDown
                            size={16}
                            className={
                                expandedInvoice ===
                                inv.id
                                    ? "rotate"
                                    : ""
                            }
                        />

                        {inv.invoice_number}

                    </div>

                </td>

                <td>
                    {new Date(
                        inv.date_created
                    ).toLocaleDateString()}
                </td>

                <td>
                    $
                    {Number(
                        inv.amount
                    ).toFixed(2)}
                </td>

                <td>
                    {new Date(
                        inv.due_date
                    ).toLocaleDateString()}
                </td>

                <td>

                    <span
                        className={`table-status ${getInvoiceStatus(inv).toLowerCase()}`}
                    >
                        {getInvoiceStatus(inv)}
                    </span>

                </td>

            </tr>

            {/* DETAILS */}

            {expandedInvoice ===
                inv.id && (

                <tr className="invoice-details-row">

                    <td colSpan={5}>

                        {inv.pos_sale_id ? (

                            <div className="invoice-details">

                                <h4>
                                    POS Invoice
                                </h4>

                                <p>

                                    This invoice was created through the Point of Sale (POS) system.

                                </p>

                                <p>

                                    Please refer to the POS invoice for the complete itemized breakdown.

                                </p>

                            </div>

                        ) : (

                            <div className="invoice-details">

                                <div className="detail-item">

                                    <strong>
                                        Item
                                    </strong>

                                    <span>
                                        {inv.item || "N/A"}
                                    </span>

                                </div>

                            </div>

                        )}

                        <hr />

                        <div className="detail-grid">

                            <div>

                                <strong>
                                    Amount
                                </strong>

                                <p>

                                    $
                                    {Number(
                                        inv.amount
                                    ).toFixed(2)}

                                </p>

                            </div>

                            <div>

                                <strong>
                                    Paid
                                </strong>

                                <p>

                                    $
                                    {paid.toFixed(2)}

                                </p>

                            </div>

                            <div>

                                <strong>
                                    Outstanding
                                </strong>

                                <p>

                                    $
                                    {outstanding.toFixed(
                                        2
                                    )}

                                </p>

                            </div>

                            <div>

                                <strong>
                                    Due Date
                                </strong>

                                <p>

                                    {new Date(
                                        inv.due_date
                                    ).toLocaleDateString()}

                                </p>

                            </div>

                        </div>

                    </td>

                </tr>

            )}

        </React.Fragment>

    );

})

                ) : (

                  <tr>

                    <td colSpan="5">
                      No invoices found.
                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

        )}

<div className="filter-summary-grid">

  <div className="filter-summary-card">

    <span>Invoices</span>

    <strong>
      {filteredInvoices.length}
    </strong>

  </div>

  <div className="filter-summary-card">

    <span>Total Amount</span>

    <strong>
      $
      {filteredInvoiceTotal.toLocaleString(
        "en-US",
        {
          minimumFractionDigits: 2,
        }
      )}
    </strong>

  </div>

  <div className="filter-summary-card">

    <span>Outstanding</span>

    <strong className="danger-text">

      $
      {filteredOutstanding.toLocaleString(
        "en-US",
        {
          minimumFractionDigits: 2,
        }
      )}

    </strong>

  </div>

</div>


        {/* ===================================== */}
        {/* PAYMENTS */}
        {/* ===================================== */}

        {selectedTab === "payments" && (

          <div className="modern-table-card">

            <div className="card-header">
              <h3>Payments</h3>
            </div>

<div className="inline-filters">

  <button
    className={
      paymentFilter === "all"
        ? "filter-active"
        : ""
    }
    onClick={() =>
      setPaymentFilter("all")
    }
  >
    All Time
  </button>

  <button
    className={
      paymentFilter === "current"
        ? "filter-active"
        : ""
    }
    onClick={() =>
      setPaymentFilter("current")
    }
  >
    Current Month
  </button>

  <button
    className={
      paymentFilter === "previous"
        ? "filter-active"
        : ""
    }
    onClick={() =>
      setPaymentFilter("previous")
    }
  >
    Previous Month
  </button>

  <button
    className={
      paymentFilter === "custom"
        ? "filter-active"
        : ""
    }
    onClick={() =>
      setPaymentFilter("custom")
    }
  >
    Custom
  </button>

</div>

{paymentFilter === "custom" && (

  <div className="date-range-inline">

    <input
      type="date"
      value={paymentStartDate}
      onChange={(e) =>
        setPaymentStartDate(
          e.target.value
        )
      }
    />

    <input
      type="date"
      value={paymentEndDate}
      onChange={(e) =>
        setPaymentEndDate(
          e.target.value
        )
      }
    />

  </div>

)}


<div className="filter-summary-grid">

  <div className="filter-summary-card">

    <span>Payments</span>

    <strong>
      {filteredPayments.length}
    </strong>

  </div>

  <div className="filter-summary-card">

    <span>Total Paid</span>

    <strong className="success-text">

      $
      {filteredPaymentTotal.toLocaleString(
        "en-US",
        {
          minimumFractionDigits: 2,
        }
      )}

    </strong>

  </div>

</div>

            <table className="modern-account-table">

              <thead>

                <tr>
                  <th>Invoice</th>
                  <th>Date</th>
                  <th>Mode</th>
                  <th>Amount</th>
                </tr>

              </thead>

              <tbody>

                {filteredPayments.length > 0 ? (

filteredPayments.map((payment) => (

<React.Fragment key={payment.id}>

<tr
    className="invoice-row"
    onClick={() =>
        togglePayment(payment.id)
    }
>

    <td>

        <div className="invoice-expand">

            <ChevronDown
                size={16}
                className={
                    expandedPayment === payment.id
                        ? "rotate"
                        : ""
                }
            />

            {payment.invoice_number}

        </div>

    </td>

    <td>

        {new Date(
            payment.payment_date
        ).toLocaleDateString()}

    </td>

    <td>

        {payment.mode || "Cash"}

    </td>

    <td>

        $
        {Number(
            payment.amount
        ).toFixed(2)}

    </td>

</tr>

{expandedPayment === payment.id && (

<tr className="invoice-details-row">

<td colSpan={4}>

<div className="detail-grid">

<div>

<strong>
Invoice
</strong>

<p>
{payment.invoice_number}
</p>

</div>

<div>

<strong>
Payment Date
</strong>

<p>

{new Date(
payment.payment_date
).toLocaleDateString()}

</p>

</div>

<div>

<strong>
Payment Method
</strong>

<p>

{payment.mode}

</p>

</div>

<div>

<strong>
Amount
</strong>

<p>

$
{Number(
payment.amount
).toFixed(2)}

</p>

</div>

<div>

<strong>
Remarks
</strong>

<p>

{payment.remarks || "None"}

</p>

</div>

</div>

</td>

</tr>

)}

</React.Fragment>

))

                ) : (

                  <tr>

                    <td colSpan="4">
                      No payments found.
                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

        )}

      </div>
<NewInvoiceModalModern
  isOpen={showInvoiceModal}
  onClose={() => setShowInvoiceModal(false)}
  client={client}
  onSuccess={loadData}
/>

<NewPaymentModalModern 
 isOpen={showPaymentModal}
  onClose={() => setShowPaymentModal(false)}
  client={client}
  invoices={invoices}
  payments={payments}
  onSuccess={loadData}
/>

<StatementExportModal
  isOpen={showStatementModal}
  onClose={() =>
    setShowStatementModal(false)
  }
  client={client}
  invoices={invoices}
  payments={payments}
  totalOutstanding={totalOutstanding}
  totalPaid={totalPaid}
/>

    </div>
  );
};

export default ClientAccount;